// Rentabilidad por producto (SKU).
// Agrega items vendidos en pedidos one-shot pagados/entregados.
// Cruza con productos para obtener costo_unitario y calcular margen.

import { useEffect, useMemo, useState } from 'react'
import AdminLayout from '../../components/AdminLayout'
import AnalisisIA from '../../components/AnalisisIA'
import { supabase } from '../../lib/supabase'

const fmtUYU = (n) => `$${Math.round(Number(n || 0)).toLocaleString('es-UY')}`
const fmtPct = (n) => `${(Number(n || 0) * 100).toFixed(1)}%`

export default function RentabilidadProducto() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [filas, setFilas] = useState([])
  const [orden, setOrden] = useState('margen_total')   // margen_total | unidades | ingresos | margen_pct
  const [meses, setMeses] = useState(3)

  useEffect(() => { cargar() }, [meses])

  async function cargar() {
    setLoading(true)
    setError(null)
    try {
      const desde = new Date()
      desde.setMonth(desde.getMonth() - meses)
      desde.setDate(1)
      const desdeStr = desde.toISOString().slice(0, 10)

      const [{ data: peds, error: pe }, { data: prods, error: pre }] = await Promise.all([
        // Rentabilidad producto: solo pedidos reales (excluye es_prueba)
        supabase
          .from('pedidos')
          .select('id, items, estado, pagado_at, created_at')
          .in('estado', ['pagado', 'en_armado', 'en_ruta', 'entregado'])
          .eq('es_prueba', false)
          .gte('created_at', desdeStr),
        supabase
          .from('productos')
          .select('sku, nombre, presentacion, costo_unitario, categoria, tier'),
      ])
      if (pe) throw pe
      if (pre) throw pre

      const prodMap = new Map((prods || []).map(p => [p.sku, p]))

      // Agregar items por sku
      const agg = new Map()
      ;(peds || []).forEach(pedido => {
        const items = Array.isArray(pedido.items) ? pedido.items : []
        items.forEach(it => {
          const sku = it.sku
          if (!sku) return
          const qty = Number(it.cantidad || 1)
          const precioU = Number(it.precio_unit || 0)
          const ingreso = qty * precioU
          if (!agg.has(sku)) {
            const p = prodMap.get(sku)
            agg.set(sku, {
              sku,
              nombre: p?.nombre || it.nombre || sku,
              presentacion: p?.presentacion || it.presentacion || '',
              categoria: p?.categoria || '',
              tier: p?.tier || '',
              costo_unit: Number(p?.costo_unitario || 0),
              unidades: 0,
              ingresos: 0,
              costo_total: 0,
              n_pedidos: 0,
            })
          }
          const r = agg.get(sku)
          r.unidades += qty
          r.ingresos += ingreso
          r.costo_total += qty * r.costo_unit
          r.n_pedidos += 1
        })
      })

      const filasArr = Array.from(agg.values()).map(r => {
        const margen = r.ingresos - r.costo_total
        const pct = r.ingresos > 0 ? margen / r.ingresos : 0
        return { ...r, margen_total: margen, margen_pct: pct }
      })
      setFilas(filasArr)
    } catch (e) {
      setError(e.message || String(e))
    } finally {
      setLoading(false)
    }
  }

  const ordenadas = useMemo(() => {
    const arr = [...filas]
    arr.sort((a, b) => (b[orden] ?? 0) - (a[orden] ?? 0))
    return arr
  }, [filas, orden])

  const totales = useMemo(() => {
    const t = { unidades: 0, ingresos: 0, costo_total: 0, margen_total: 0 }
    filas.forEach(r => {
      t.unidades += r.unidades
      t.ingresos += r.ingresos
      t.costo_total += r.costo_total
      t.margen_total += r.margen_total
    })
    t.margen_pct = t.ingresos > 0 ? t.margen_total / t.ingresos : 0
    return t
  }, [filas])

  return (
    <AdminLayout>
      <div className="page">
        <h1>🏆 Rentabilidad por producto</h1>
        <p className="muted">Unidades vendidas, ingreso, costo y margen por SKU. Solo pedidos one-shot pagados/entregados.</p>

        <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
          <label style={{ fontSize: 13, color: '#374151', fontWeight: 600 }}>
            Período:
            <select value={meses} onChange={e => setMeses(Number(e.target.value))}
              style={{ marginLeft: 8, padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13 }}>
              <option value={1}>Último mes</option>
              <option value={3}>Últimos 3 meses</option>
              <option value={6}>Últimos 6 meses</option>
              <option value={12}>Último año</option>
            </select>
          </label>
          <label style={{ fontSize: 13, color: '#374151', fontWeight: 600 }}>
            Ordenar por:
            <select value={orden} onChange={e => setOrden(e.target.value)}
              style={{ marginLeft: 8, padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: 13 }}>
              <option value="margen_total">Margen total ($)</option>
              <option value="ingresos">Ingresos ($)</option>
              <option value="unidades">Unidades vendidas</option>
              <option value="margen_pct">% margen</option>
            </select>
          </label>
        </div>

        {/* Totales */}
        <div className="cards-grid" style={{ marginBottom: 16 }}>
          <div className="card">
            <h3>Productos vendidos</h3>
            <div className="stat">{totales.unidades}</div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>{filas.length} SKUs distintos</div>
          </div>
          <div className="card">
            <h3>Ingresos</h3>
            <div className="stat" style={{ color: '#10B981' }}>{fmtUYU(totales.ingresos)}</div>
          </div>
          <div className="card">
            <h3>Costo de mercadería</h3>
            <div className="stat" style={{ color: '#ef4444' }}>{fmtUYU(totales.costo_total)}</div>
          </div>
          <div className="card">
            <h3>Margen bruto</h3>
            <div className="stat" style={{ color: '#1a3461' }}>{fmtUYU(totales.margen_total)}</div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>{fmtPct(totales.margen_pct)}</div>
          </div>
        </div>

        {error && <div style={{ padding: 12, background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, color: '#991b1b', marginBottom: 12 }}>{error}</div>}
        {loading && <div className="ap-loading">Cargando…</div>}

        {!loading && ordenadas.length === 0 && (
          <div className="ap-empty">
            <div style={{ fontSize: 48 }}>📦</div>
            <h3>Sin ventas en el período seleccionado</h3>
          </div>
        )}

        {!loading && ordenadas.length > 0 && (
          <div style={{ overflowX: 'auto', background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb' }}>
            <table className="usu-table">
              <thead>
                <tr>
                  <th>Producto</th>
                  <th style={{ textAlign: 'center' }}>Unid.</th>
                  <th style={{ textAlign: 'right' }}>Costo unit.</th>
                  <th style={{ textAlign: 'right' }}>Ingresos</th>
                  <th style={{ textAlign: 'right' }}>Costo total</th>
                  <th style={{ textAlign: 'right' }}>Margen $</th>
                  <th style={{ textAlign: 'right' }}>Margen %</th>
                </tr>
              </thead>
              <tbody>
                {ordenadas.map(r => (
                  <tr key={r.sku}>
                    <td>
                      <div style={{ fontWeight: 700, color: '#1a3461' }}>{r.nombre}</div>
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>
                        {r.sku} {r.presentacion && `· ${r.presentacion}`} {r.categoria && `· ${r.categoria}`}
                      </div>
                    </td>
                    <td style={{ textAlign: 'center', fontWeight: 700 }}>{r.unidades}</td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: 12, color: '#6b7280' }}>{fmtUYU(r.costo_unit)}</td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#10B981', fontWeight: 600 }}>{fmtUYU(r.ingresos)}</td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: '#ef4444' }}>{fmtUYU(r.costo_total)}</td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700 }}>{fmtUYU(r.margen_total)}</td>
                    <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                      <span style={{
                        padding: '2px 8px', borderRadius: 999,
                        background: r.margen_pct >= 0.4 ? '#d1fae5' : r.margen_pct >= 0.2 ? '#fef3c7' : '#fee2e2',
                        color: r.margen_pct >= 0.4 ? '#065f46' : r.margen_pct >= 0.2 ? '#92400e' : '#991b1b',
                        fontSize: 12, fontWeight: 700,
                      }}>
                        {fmtPct(r.margen_pct)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <AnalisisIA
          reportType="rentabilidad_producto"
          data={{ totales, productos: ordenadas.slice(0, 20), periodo_meses: meses }}
        />

        <div style={{ marginTop: 20, padding: 14, background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 10, fontSize: 12, color: '#78350f' }}>
          <strong>📌 Notas:</strong> Costo unitario tomado del catálogo de productos.
          Las ventas dentro de kits de suscripción aún no están incluidas en este reporte (solo pedidos one-shot).
        </div>
      </div>

      <style>{`
        .usu-table { width: 100%; border-collapse: collapse; font-size: 14px; }
        .usu-table thead th {
          text-align: left; padding: 12px 14px;
          background: #f8fafc; border-bottom: 1px solid #e5e7eb;
          font-size: 12px; text-transform: uppercase; color: #6b7280;
          font-weight: 700; letter-spacing: .03em;
        }
        .usu-table tbody td {
          padding: 12px 14px; border-bottom: 1px solid #f1f5f9; vertical-align: top;
        }
        .usu-table tbody tr:hover { background: #f8fafc; }
        .ap-loading { padding: 40px; text-align: center; color: #6b7280; }
        .ap-empty { padding: 60px 20px; text-align: center; color: #6b7280; }
        .ap-empty h3 { color: #1a3461; margin: 16px 0 8px; }
      `}</style>
    </AdminLayout>
  )
}
